import { z } from 'zod';
import {
  createModel,
  currencySchema,
  imageWithUploadSchema,
  partySchema,
  QueryMany,
  QueryOne,
  tenantSchema,
} from '../../common';
import { nameSchema, objectIdSchema } from '../../validation';
import { agreementTypeSchema } from './agreement.type.model';
import { quantitySchema } from '../../validation';
import { agreementTermsSchema } from './agreement.terms.model';
import { agreementDatesSchema } from './agreement.date.model';
import { agreementStatusSchema } from './agreement.status.model';
import { UTagSharedSchema } from '../utag';

/** `Agreement db model` */
export const AgreementSchema = createModel({
  /** the title of the agreement. Such as Amazon Contract 2026 */
  title: nameSchema,
  /** status of the Agreement
   *
   *  - `new ` = means the just has been created or not yet have been approved
   *  - `active` = means approved by the customer and **this is the one we will use to display the price for**.
   *  - `canceled` = rejected by the customer or rejected by us for any reason.
   *  - `terminated` = means the agreement **has started** but terminated before completing agreement `end` date
   *  - `expired ` = this contract has ended successfully but has not been renewed
   */
  status: agreementStatusSchema,

  /** type of the agreement. is it quotation only or quotation with contract */
  type: agreementTypeSchema,

  /** the currency of the Agreement */
  currency: currencySchema,

  /**
   * the organization that this agreement belongs to.
   *
   * Denormalized "primary org" Party snapshot for owner-side queries
   * (e.g. "all agreements I own"). Kept alongside the bilateral
   * {@link AgreementSchema.shape.parties} field — NOT deprecated. The
   * owner-org is whichever party in `parties[]` matches `org._id`.
   *
   * Uses {@link partySchema} (relations-v2 shape): writers stamp this
   * with `parties[0]` (the org-side party) so consumers get the full
   * Party snapshot (`target`, `roles[]`, `ids[]`, `name`) — the same
   * data shape used everywhere else in the relations-v2 model.
   */
  org: partySchema,

  /**
   * The two parties this Agreement is between (unordered pair).
   *
   * REQUIRED tuple of exactly 2 — every Agreement is bilateral by
   * construction (relations-v2). The primary lookup
   * `AgreementService.findActiveBetween(partyAId, partyBId)` queries
   * `{ 'tenants.id': { $all: [partyAId, partyBId] }, status: 'active' }`
   * (denormalized to `tenants[]` so MongoDB can serve it from a single
   * compound multikey index — see {@link AgreementSchema.shape.tenants}).
   *
   * Each party is a {@link partySchema} snapshot carrying:
   *   - `_id`   — primary id of the source doc (Org / User / Client / Profile).
   *   - `target` — which collection holds the source doc (clean 7-value enum).
   *   - `roles[]` — multi-valued network roles (`carrier_company`, `broker`,
   *      `business_customer`, …). `roles[0]` is the primary-role display
   *      convention. Drives "did we agree as buyer-of-broker-X or
   *      carrier-of-org-X?" disambiguation.
   *   - `ids[]` — identifier-equivalents (`{type, id}`); always contains
   *      the primary id-entry as the first entry. Additional entries
   *      (`phone`, `email`, …) flow into `tenants[]` so we can also lookup
   *      agreements by late-binding identifiers.
   *
   * Used by:
   *   - `OfferService.create` to snapshot per-target pricing onto each
   *     `offerTargetSchema.pricing.agreement` row.
   *   - `OrderService.pushAcceptedContract` to copy the snapshot onto the
   *     new `Order.contracts[i].agreement`.
   *   - `RouteService.validate` to confirm a directed Route bound to an
   *     Agreement matches the Agreement's parties.
   *
   * Built on the writer side via `PartyResolverService.makeParty`. New
   * agreement creation REQUIRES the API caller to provide the
   * counterparty (`AgreementCreateAPI.req.counterparty`) — the org-side
   * party is materialized from the API's `orgId`.
   *
   * Indexes (operator-applied; documented in `db.service.ts`):
   *   - `{ 'parties._id': 1, status: 1 }` — kept for back-compat
   *     `findActiveBetween` reads issued before the cutover.
   *   - `{ 'tenants.id': 1 }` and `{ 'tenants.id': 1, status: 1 }` —
   *     primary path for new code.
   */
  parties: z.tuple([partySchema, partySchema]),

  /**
   * Optional `Connection` doc id snapshot.
   *
   * When the Agreement is created, `AgreementService.createNewAgreementAPI`
   * calls `ConnectionService.findOrCreate(orgParty, counterpartyParty)`
   * and stamps the resulting `Connection._id` here. Mirrored as a
   * `connection_id` entry in {@link AgreementSchema.shape.tenants} so the
   * agreement is reachable from any party who is a tenant on that
   * Connection.
   *
   * Optional because legacy docs (pre-`relations-v2`) never wrote a
   * Connection alongside the Agreement; the migration backfills these
   * where possible and leaves the field unset where the relationship
   * cannot be resolved.
   */
  connectionId: objectIdSchema.optional(),

  /**
   * Flat visibility set for the Agreement, mirroring the
   * `Order.tenants[]` pattern.
   *
   * Computed from {@link AgreementSchema.shape.parties} (every entry in
   * each `party.ids[]`) plus the {@link AgreementSchema.shape.connectionId}
   * snapshot. Index `{ 'tenants.id': 1 }` lets us answer "what agreements
   * involve this party id?" in a single seek — the canonical filter for
   * `getManyAgreementAPI({ partyId })` (workspace + agreement-tab UIs).
   *
   * Always `default([])`; populated by `AgreementTenantsBuilder.recompute`
   * on every writer (create / update). Idempotent: two writers that
   * produce the same `(parties, connectionId)` set produce the same
   * `tenants[]`.
   */
  tenants: z.array(tenantSchema).default([]),

  /**
   * Lifecycle window of the Agreement.
   *
   * REQUIRED — every agreement has an explicit `start` / `end` pair.
   * The previous schema marked this optional to accommodate legacy
   * docs (some sales-led inserts skipped the dates entirely); the
   * relations-v2 cycle backfilled missing windows via
   * `20260603000300-relations-v2-agreement-dates-required.ts` so the
   * field can now be tightened. New writers MUST pass `start` + `end`
   * + `autoRenew`; the DTO computes `durationInDays` server-side.
   */
  dates: agreementDatesSchema,

  /** a pdf image of the singed contract */
  contractPDF: imageWithUploadSchema.optional(),

  /** a pdf image of the singed contract */
  quotationPDF: imageWithUploadSchema.optional(),

  /** payment terms for the agreement  */
  terms: agreementTermsSchema,

  /**
   * Routes that belong to this Agreement live in the `Route` collection with
   * `Route.agreementId` set to this `Agreement._id`. There is no embedded
   * `routes[]` array on the Agreement any more — query the Route collection
   * via `RouteService.findByAgreement(agreementId)`.
   */
  monthlyVehicleVolume: quantitySchema,
  avgDailyVehicleVolume: quantitySchema,

  UTags: z.array(UTagSharedSchema),
});

/** `agreement db model` */
export type Agreement = z.infer<typeof AgreementSchema.model>;
export type AgreementData = z.infer<typeof AgreementSchema.data>;
export type AgreementPartial = z.infer<typeof AgreementSchema.partial>;
export type AgreementQueryMany = QueryMany<Agreement>;
export type AgreementQueryOne = QueryOne<Agreement>;
